import { useMemo } from 'react';
import { gql } from '@apollo/client';

import type { Extension, GetContextTokenOptions } from '@atlassian/forge-ui';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { fg } from '@confluence/feature-gating';

import type { ForgeUIExtensionDataType } from '../types.ts';
import { getModuleKey } from '../utils/appData.ts';

/**
 * Function type for retrieving Forge Context Tokens.
 * Supports optional overrides for embedded macro scenarios.
 */
type GetContextToken = (options?: GetContextTokenOptions) => Promise<string>;

// Explicit types to satisfy TS inference for the mutation result
export type UseGetContextTokenMutationData = {
	confluence_generateForgeContextToken?: {
		success: boolean;
		errors?: Array<{
			message: string;
			code?: string;
		}>;
		forgeContextToken?: {
			jwt?: string;
			expiresAt?: string | null;
			extensionId?: string | null;
		};
	};
};

export type UseGetContextTokenMutationVariables = {
	cloudId: string;
	input: any;
};

// Cache types and helpers
export type CachedTokenPromise = {
	promise: Promise<string>;
	expiresAt?: number;
	issuedAt: number;
};

class ContextTokenCache {
	private static instance: ContextTokenCache;
	private promiseCache: Map<string, CachedTokenPromise>;

	static getInstance() {
		if (!ContextTokenCache.instance) {
			ContextTokenCache.instance = new ContextTokenCache();
		}
		return ContextTokenCache.instance;
	}

	private constructor() {
		this.promiseCache = new Map<string, CachedTokenPromise>();
	}

	get(key: string): CachedTokenPromise | undefined {
		return this.promiseCache.get(key);
	}

	set(key: string, value: CachedTokenPromise): void {
		this.promiseCache.set(key, value);
	}

	delete(key: string): void {
		this.promiseCache.delete(key);
	}
}

const EXTRA_BUFFER_MS = 30000; // extra 30 seconds buffer for clock skew

// GraphQL mutation to get context token
const UseGetContextTokenMutation = gql`
	mutation useGetContextTokenMutation(
		$cloudId: ID!
		$input: ConfluenceForgeContextTokenRequestInput!
	) {
		confluence_generateForgeContextToken(cloudId: $cloudId, input: $input) {
			success
			errors {
				message
			}
			forgeContextToken {
				jwt
				expiresAt
				extensionId
			}
		}
	}
`;

/**
 * Hook to generate a context token function for Forge extensions in Confluence
 *
 * @param extension - The Forge extension configuration
 * @param extensionData - The extension context data
 * @param localId - The local identifier for this extension instance
 * @param contextIds - The context IDs for this extension instance
 * @returns A function that can be called to retrieve context tokens
 */
export const useGetContextToken = (
	extension: Extension,
	extensionData: ForgeUIExtensionDataType,
	localId: string | undefined,
	contextIds: string[],
	cachingEnabled: boolean = false,
): GetContextToken => {
	const { cloudId } = useSessionData();
	const [runMutation] = useMutationInterop<
		UseGetContextTokenMutationData,
		UseGetContextTokenMutationVariables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(UseGetContextTokenMutation, {
		client: getAGGClient(),
	});

	return useMemo(() => {
		const fetchTokenFromServer = async (
			effectiveContextIds: string[],
			effectiveExtension: Extension,
			effectiveLocalId: string | undefined,
			cache: ContextTokenCache,
			cacheKey: string,
		): Promise<string> => {
			try {
				// Build the input according to the schema
				const input = {
					contextIds: effectiveContextIds,
					extensionSpecificContexts: {
						appVersion: effectiveExtension.appVersion,
						context: {
							appVersion: effectiveExtension.appVersion,
							environmentId: effectiveExtension.environmentId,
							environmentType: effectiveExtension.environmentType,
							siteUrl: window?.location?.origin,
							extension: {
								location: undefined,
								content: extensionData?.content
									? {
											id: extensionData.content.id,
											type: extensionData.content.type,
											subtype: extensionData.content.subtype,
										}
									: undefined,
								space: extensionData?.space
									? {
											id: extensionData.space.id,
											key: extensionData.space.key,
										}
									: undefined,
								...(extensionData?.template?.id && {
									template: { id: extensionData.template.id },
								}),
								type: extensionData.type,
								selectedText: extensionData?.selectedText,
							},
							localId: effectiveLocalId,
							moduleKey: getModuleKey(effectiveExtension),
						},
						extensionId: effectiveExtension.id,
						extensionType: effectiveExtension.type,
						installationId: effectiveExtension.installationId,
					},
				};

				const { data } = await runMutation({
					variables: {
						cloudId,
						input,
					},
				});

				if (!data?.confluence_generateForgeContextToken?.success) {
					const errors = data?.confluence_generateForgeContextToken?.errors || [];
					const errorMessage = errors.map((e) => e.message).join(', ') || 'Unknown error';
					throw new Error(`Token generation failed: ${errorMessage}`);
				}

				const forgeContextToken = data.confluence_generateForgeContextToken.forgeContextToken;
				if (!forgeContextToken?.jwt) {
					throw new Error('No JWT token received from GraphQL endpoint');
				}

				// Update cache with expiry info if available
				if (cachingEnabled) {
					const cachedEntry = cache.get(cacheKey);
					if (cachedEntry && forgeContextToken.expiresAt) {
						cachedEntry.expiresAt = Number(forgeContextToken.expiresAt);
					}
				}

				return forgeContextToken.jwt;
			} catch (error: any) {
				// Remove from cache on error so next call will retry
				if (cachingEnabled) {
					cache.delete(cacheKey);
				}

				throw new Error(`Failed to get context token: ${error.message}`);
			}
		};

		// Original implementation - only supports contextIdsOverride
		async function getForgeContextTokenLegacy(options?: GetContextTokenOptions): Promise<string> {
			if (!cloudId) {
				throw new Error('CloudId is required but not available');
			}

			const { contextIdsOverride } = options ?? {};
			const effectiveContextIds = contextIdsOverride ?? contextIds;

			const cacheKey = JSON.stringify({
				id: localId || extension.id,
				data: extensionData,
				contextIds: effectiveContextIds,
			});
			const cache = ContextTokenCache.getInstance();
			const cached = cache.get(cacheKey);
			if (cachingEnabled) {
				if (cached) {
					if (typeof cached.expiresAt !== 'number') {
						return cached.promise;
					}
					if (Date.now() + EXTRA_BUFFER_MS < cached.expiresAt) {
						return cached.promise;
					}
				}

				const issuedAt = Date.now();
				const tokenPromise = fetchTokenFromServer(
					effectiveContextIds,
					extension,
					localId,
					cache,
					cacheKey,
				);

				cache.set(cacheKey, {
					promise: tokenPromise,
					expiresAt: undefined,
					issuedAt,
				});

				return tokenPromise;
			}

			return fetchTokenFromServer(effectiveContextIds, extension, localId, cache, cacheKey);
		}

		// New implementation - supports both contextIdsOverride and extensionOverride for embedded macros
		async function getForgeContextTokenWithExtensionOverride(
			options?: GetContextTokenOptions,
		): Promise<string> {
			if (!cloudId) {
				throw new Error('CloudId is required but not available');
			}

			const { contextIdsOverride, extensionOverride } = options ?? {};

			// Use override values if provided (for embedded macros), otherwise use parent values
			const effectiveExtension = extensionOverride?.extension ?? extension;
			const effectiveLocalId = extensionOverride?.localId ?? localId;
			const effectiveContextIds = contextIdsOverride ?? contextIds;

			const cacheKey = JSON.stringify({
				id: effectiveLocalId || effectiveExtension.id,
				contextIds: effectiveContextIds,
			});
			const cache = ContextTokenCache.getInstance();
			const cached = cache.get(cacheKey);
			if (cachingEnabled) {
				if (cached) {
					if (typeof cached.expiresAt !== 'number') {
						return cached.promise;
					}
					if (Date.now() + EXTRA_BUFFER_MS < cached.expiresAt) {
						return cached.promise;
					}
				}

				const issuedAt = Date.now();
				const tokenPromise = fetchTokenFromServer(
					effectiveContextIds,
					effectiveExtension,
					effectiveLocalId,
					cache,
					cacheKey,
				);

				cache.set(cacheKey, {
					promise: tokenPromise,
					expiresAt: undefined,
					issuedAt,
				});

				return tokenPromise;
			}

			return fetchTokenFromServer(
				effectiveContextIds,
				effectiveExtension,
				effectiveLocalId,
				cache,
				cacheKey,
			);
		}

		return fg('platform_embedded_macro_fct_override')
			? getForgeContextTokenWithExtensionOverride // once FG rolled out rename to getForgeContextToken and remove the legacy function
			: getForgeContextTokenLegacy;
	}, [extension, extensionData, localId, cloudId, runMutation, contextIds, cachingEnabled]);
};
